<template>
  <div class="flex flex-col gap-4">
    <template v-if="isCurrentItemOwner">
      <div class="flex flex-col sm:flex-row sm:items-center gap-2 justify-between">
        <ShareSecurity />
      </div>
      <div
        v-if="showEmptyInvitationMessage"
        class="p-3 rounded-lg border border-slate-200 dark:border-zinc-700 bg-slate-100 dark:bg-zinc-800 font-semibold text-sm"
      >
        You haven’t invited people via email, yet.
      </div>
      <div
        v-if="sharing === ItemSharingMode.PASSWORD"
        class="p-3 rounded-lg border border-slate-200 dark:border-zinc-700 bg-slate-50 dark:bg-zinc-800 font-semibold text-sm"
      >
        <SharePassword />
      </div>

      <HorizontalDivider />
      <ShareInviteEmail />
      <HorizontalDivider />
    </template>

    <div class="flex items-center justify-between">
      <ShareExpiration v-if="isCurrentItemOwner" @custom-setting-enabled="showShareExpirationCustom = true" :showHighlighter="expireButtonHighlighter" />
      <button data-testid="copy-link-to-clipboard" class="flex gap-1.5 items-center text-base font-semibold text-blue-500" @click.prevent="copy()">
        <Icon name="general.copy" size="base" />
        <span>Copy link</span>
      </button>
    </div>

    <template v-if="isCurrentItemOwner">
      <ShareExpirationCustom v-if="showShareExpirationCustom || expires !== ItemExpiresMode.NEVER" />
      <div
        v-if="isItemsRetentionEnabled()"
        class="text-sm dark:text-zinc-300 p-3 bg-slate-100 dark:bg-zinc-800 border border-slate-200 dark:border-zinc-700 rounded-lg flex flex-col gap-3"
      >
        <p>
          Your admin has set the File Retention policy to automatically {{ retentionType }} <strong>{{ itemsRetentionHumanReadable }}</strong> after their
          creation date.
        </p>
        <p>This policy overrides any expiration settings you choose.</p>
      </div>
    </template>
  </div>
</template>
<script setup lang="ts">
import { computed, ref, Ref, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useClipboard } from '@vueuse/core';

import { useOrganizationStore } from '@/store/useOrganizationStore';
import { useToast } from '@/composables/useToast';
import { TrackEvents, useViewerTrack } from '@/modules/viewer/composables/useViewerTrack';

import { Icon } from '@/storybook/icon';

import HorizontalDivider from '@/storybook/utilities/HorizontalDivider.vue';

import { useViewerItemStore, ItemSharingMode, ItemExpiresMode } from '@/modules/viewer/store/useViewerItemStore';
import { useRetention } from '@/modules/viewer/composables/useRetention';
import { useUser } from '@/modules/viewer/composables/useUser';

import ShareSecurity from './general/ShareSecurity.vue';
import SharePassword from './general/SharePassword.vue';
import ShareInviteEmail from './general/ShareInviteEmail.vue';
import ShareExpiration from './general/ShareExpiration.vue';
import ShareExpirationCustom from './general/ShareExpirationCustom.vue';

defineProps<{
  expireButtonHighlighter?: boolean;
}>();

const viewerItemStore = useViewerItemStore();
const { success } = useToast();
const { trackItemOwner } = useViewerTrack();

const { shareLink, sharing, sharingSpecificUsers, expires } = storeToRefs(viewerItemStore);
const { itemsRetentionHumanReadable, itemsRemoveAction } = storeToRefs(useOrganizationStore());
const { copy, copied } = useClipboard({ source: shareLink as Ref });
const { isItemsRetentionEnabled } = useRetention();
const { isCurrentItemOwner } = useUser();

const retentionType = computed(() => {
  if (itemsRemoveAction.value === 'delete') {
    return 'and permanently delete files';
  }
  return 'move files to Trash';
});

const showShareExpirationCustom = ref(false);

const showEmptyInvitationMessage = computed(() => {
  if (!sharing.value) {
    return false;
  }

  const inProperView = [ItemSharingMode.SPECIFIC_USER_VIEW, ItemSharingMode.SPECIFIC_USER_EDIT].indexOf(sharing.value) > -1;
  const emptySharingSpecificUsers = sharingSpecificUsers.value && sharingSpecificUsers.value.length === 0;
  if (inProperView && emptySharingSpecificUsers) {
    return true;
  }

  return false;
});

watch(copied, res => {
  if (!res) {
    return;
  }

  trackItemOwner(TrackEvents.VIEWER_COPY_LINK_CLICK, { source: 'share_modal' });
  success('Share link copied to clipboard', { icon: 'general.copy' });
});
</script>
